import { useState } from 'react';
import { Modal } from '../../components/Modal';
import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { useLearningPlanMutation, useTeacherCandidates } from '../../api/learning-plans';
import { learningPlanError, planLabel, weekdayNames } from './labels';
import { optionCard } from './PlanManager';
import type { LearningPlan, PlanTeacherCandidate, PlanTimetable } from '../../types/learning-plans';

type Override = NonNullable<PlanTimetable['overrides']>[number] & { uiId: string };

const endTime = (start: string, minutes: number): string => {
  const [h, m] = start.split(':').map(Number);
  const n = h * 60 + m + minutes;
  return `${String(Math.floor(n / 60) % 24).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
};
const today = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);

// Who suits the family first: teachers who already said yes, then those the
// automatic search would have asked, then everyone else with the reason.
const rank = (t: PlanTeacherCandidate) => (t.response === 'accepted' ? 0 : t.eligible ? 1 : 2);

/**
 * Setting up an unpaid request in one step: choose each track's teacher and
 * weekly times, then hold them for the family to pay. Choosing a teacher here
 * assigns them; they are notified, not asked. A teacher who declined the
 * request is not offered.
 */
export function PlanSetup({ request: r, onClose, onDone }: {
  request: LearningPlan;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const tracks = r.offer.tracks.filter((t) => r.balances.some((b) => b.trackKey === t.key));
  const trial = r.kind === 'trial';
  const duration = (key: string) => {
    const t = tracks.find((x) => x.key === key);
    return (trial ? t?.trialDurationMinutes : t?.durationMinutes) ?? 60;
  };
  const candidates = useTeacherCandidates(r.id, true);
  const hold = useLearningPlanMutation();
  const [startDate, setStartDate] = useState(r.startDate ?? '');
  const [search, setSearch] = useState('');
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [schedule, setSchedule] = useState(() =>
    tracks.map((t) => ({
      trackKey: t.key,
      tutorId: r.schedule?.find((s) => s.trackKey === t.key)?.tutorId ?? '',
      times: r.schedule?.find((s) => s.trackKey === t.key)?.times ?? Array.from({ length: trial ? 1 : t.weeklyMeetings }, (_, i) => ({
        weekday: r.windows[i]?.weekday ?? i,
        startLocal: r.windows[i]?.startLocal ?? '16:00',
      })),
    })),
  );
  const change = (trackIndex: number, patch: Partial<(typeof schedule)[number]>) => {
    setSchedule(schedule.map((s, i) => (i === trackIndex ? { ...s, ...patch } : s)));
    hold.reset();
  };
  const timetable: PlanTimetable = {
    startDate,
    tracks: schedule.map((s) => ({
      ...s,
      times: s.times.map((t) => ({ ...t, endLocal: endTime(t.startLocal, duration(s.trackKey)) })),
    })),
    overrides: overrides.map(({ uiId: _uiId, ...o }) => o),
  };
  const ready = Boolean(startDate) && schedule.every((s) => s.tutorId && s.times.every((t) => t.startLocal));

  return (
    <Modal
      lang="ar"
      dir="rtl"
      className="learning-plans-ar"
      open
      width="lg"
      title="تعيين المعلّم والجدول"
      description={`${r.studentName} · ${r.offer.nameAr || r.offer.nameEn} · ${planLabel(r.kind)}`}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-2" role="status">
            {ready ? 'تُحجز المواعيد مؤقتًا، ويصل إشعار للمعلّم وللأسرة لإتمام الدفع.' : 'اختر تاريخ البداية والمعلّم والمواعيد.'}
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>إغلاق</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!ready || hold.isPending}
              onClick={() => hold.mutate(
                { path: `/admin/learning-plans/${r.id}/hold`, body: timetable },
                { onSuccess: () => onDone('تم تعيين المعلّم وحجز الجدول. أُرسل إشعار للمعلّم وللأسرة لإتمام الدفع.') },
              )}
            >
              {hold.isPending ? 'جارٍ الحجز…' : 'تعيين وحجز الجدول'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <section aria-label="الأوقات المناسبة للأسرة" className="rounded-lg border border-rule p-3">
          <p className="text-xs text-ink-3">الأوقات المناسبة للأسرة · بتوقيت الرياض</p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {r.windows.map((w) => (
              <li key={`${w.weekday}-${w.startLocal}`} className="rounded-md bg-surface-2 px-2 py-0.5 text-xs tabular-nums">
                {weekdayNames[w.weekday]} {w.startLocal}–{w.endLocal}
              </li>
            ))}
          </ul>
        </section>

        <label className="block text-sm">
          {trial ? 'تاريخ التجربة (أو أول يوم يُبحث فيه عن موعدها)' : 'بداية الدورة (4 أسابيع)'}
          <input className="input mt-1 sm:w-56" type="date" value={startDate} min={today()}
            onChange={(e) => { setStartDate(e.target.value); hold.reset(); }} />
        </label>

        {(candidates.data?.length ?? 0) > 6 * tracks.length && (
          <input className="input" type="search" value={search} placeholder="ابحث باسم المعلّم" aria-label="البحث عن معلّم"
            onChange={(e) => setSearch(e.target.value)} />
        )}
        {candidates.isLoading && <p className="text-sm text-ink-3">جارٍ تحميل المعلّمين…</p>}
        {candidates.isError && <p role="alert" className="text-sm text-danger">{learningPlanError(candidates.error, 'تعذّر تحميل المعلّمين. أعد المحاولة.')}</p>}

        {schedule.map((s, i) => {
          const track = tracks[i];
          const selectedTeacher = candidates.data?.find((t) => t.trackKey === s.trackKey && t.tutorId === s.tutorId);
          const teachers = (candidates.data ?? [])
            .filter((t) => t.trackKey === s.trackKey && t.response !== 'declined')
            .filter((t) => t.tutorId === s.tutorId || t.tutorName.toLowerCase().includes(search.trim().toLowerCase()))
            .sort((a, b) => rank(a) - rank(b));
          return (
            <section key={s.trackKey} aria-label={track.name} className="space-y-3 rounded-lg border border-rule p-4">
              <h3 className="text-sm font-semibold">
                {track.name}
                <span className="font-normal text-ink-3"> · {s.times.length} {trial ? 'حصة' : 'حصص أسبوعيًا'} · {duration(s.trackKey)} دقيقة</span>
              </h3>

              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">المعلّم</legend>
                {candidates.data && teachers.length === 0 && (
                  <p className="rounded-lg border border-dashed border-rule p-3 text-sm text-ink-2">لا يوجد معلّمون معتمدون لهذا المسار.</p>
                )}
                <div className="max-h-56 space-y-2 overflow-y-auto">
                  {teachers.map((t) => (
                    <label key={t.tutorId} data-tutor-id={t.tutorId} className={optionCard}>
                      <input type="radio" name={`tutor-${s.trackKey}`} className="sr-only" checked={s.tutorId === t.tutorId}
                        onChange={() => change(i, { tutorId: t.tutorId })} />
                      <Avatar name={t.tutorName} size="sm" />
                      <span className="flex-1">
                        <span className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-medium">{t.tutorName}</span>
                          {t.response === 'accepted' ? <Badge tone="good">وافق على الطلب</Badge>
                            : t.eligible ? <Badge tone="accent">مناسب لأوقات الأسرة</Badge> : null}
                        </span>
                        {t.blockers.length > 0 && (
                          <span className="mt-0.5 block text-xs text-ink-3">{t.blockers.map(planLabel).join(' ')}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {selectedTeacher && (
                <section aria-label="مقارنة الأوقات المتاحة" className="rounded-lg bg-surface-2 p-3 text-sm">
                  <p className="font-medium">الأوقات المتاحة · بتوقيت الرياض</p>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    {[
                      { label: `الطالب: ${r.studentName}`, windows: r.windows },
                      { label: `المعلّم: ${selectedTeacher.tutorName}`, windows: selectedTeacher.windows ?? [] },
                    ].map(({ label, windows }) => (
                      <div key={label}>
                        <p className="font-medium">{label}</p>
                        {windows.length > 0 ? (
                          <ul className="mt-1 space-y-1">
                            {windows.map((w) => (
                              <li key={`${w.weekday}-${w.startLocal}-${w.endLocal}`}>
                                {weekdayNames[w.weekday]} <span dir="ltr" className="inline-block tabular-nums">{w.startLocal}–{w.endLocal}</span>
                              </li>
                            ))}
                          </ul>
                        ) : <p className="mt-1 text-ink-3">لم تُسجّل أوقات متاحة.</p>}
                      </div>
                    ))}
                  </div>
                  {(selectedTeacher.unavailableDates?.length ?? 0) > 0 && (
                    <p className="mt-3 text-ink-2">تواريخ عدم توفر المعلّم: {selectedTeacher.unavailableDates.join('، ')}</p>
                  )}
                  <p className="mt-3 text-xs text-ink-3">هذه أوقات العمل المنشورة. يتم التحقق من تعارض الحجوزات عند حجز الجدول.</p>
                </section>
              )}

              <fieldset className="space-y-2">
                <legend className="mb-1 text-sm font-medium">{trial ? 'موعد التجربة' : 'المواعيد الأسبوعية'}</legend>
                {s.times.map((w, k) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: one row per purchased weekly meeting; rows never reorder
                  <div key={k} className="flex flex-wrap items-end gap-3 rounded-lg border border-rule p-3">
                    <fieldset>
                      <legend className="sr-only">اليوم للحصة {k + 1}</legend>
                      <div className="flex flex-wrap gap-1">
                        {weekdayNames.map((name, day) => (
                          <label key={name}
                            className="relative cursor-pointer rounded-md border border-rule px-2 py-1 text-xs has-[:checked]:border-accent has-[:checked]:bg-accent-wash has-[:checked]:font-semibold has-[:checked]:text-accent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent">
                            <input type="radio" className="sr-only" name={`weekday-${s.trackKey}-${k}`} checked={w.weekday === day}
                              onChange={() => change(i, { times: s.times.map((x, n) => (n === k ? { ...x, weekday: day } : x)) })} />
                            {name}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label className="text-sm">وقت البداية
                      <input className="input mt-1 w-32" type="time" value={w.startLocal}
                        onChange={(e) => change(i, { times: s.times.map((x, n) => (n === k ? { ...x, startLocal: e.target.value } : x)) })} />
                    </label>
                    <span className="pb-2 text-sm tabular-nums text-ink-2">
                      حتى {w.startLocal ? endTime(w.startLocal, duration(s.trackKey)) : '—'}
                    </span>
                  </div>
                ))}
              </fieldset>
            </section>
          );
        })}

        <details className="rounded-lg border border-rule p-3 text-sm">
          <summary className="cursor-pointer font-medium">حصة تقع في يوم إغلاق؟ (اختياري)</summary>
          <p className="mt-2 text-ink-2">ضع موعدًا بديلًا متفقًا عليه لحصة بعينها داخل الدورة. يبقى الجدول الأسبوعي وعدد الحصص كما هو.</p>
          {overrides.map((o) => (
            <fieldset key={o.uiId} className="my-3 grid gap-2 rounded border border-rule p-3 sm:grid-cols-2">
              {tracks.length > 1 && (
                <label className="block sm:col-span-2">المسار
                  <select className="input" value={o.trackKey}
                    onChange={(e) => setOverrides(overrides.map((x) => (x.uiId === o.uiId ? { ...x, trackKey: e.target.value } : x)))}>
                    {tracks.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                  </select>
                </label>
              )}
              {(['originalDate', 'originalStartLocal', 'startTime', 'endTime'] as const).map((key) => (
                <label key={key} className="block">
                  {{ originalDate: 'تاريخ الحصة الأصلية', originalStartLocal: 'وقتها الأصلي', startTime: 'بداية الموعد البديل', endTime: 'نهاية الموعد البديل' }[key]}
                  <input className="input" type={key === 'originalDate' ? 'date' : key === 'originalStartLocal' ? 'time' : 'datetime-local'}
                    value={key === 'startTime' || key === 'endTime' ? o[key].slice(0, 16) : o[key]}
                    onChange={(e) => setOverrides(overrides.map((x) => (x.uiId === o.uiId
                      ? { ...x, [key]: key === 'startTime' || key === 'endTime' ? `${e.target.value}:00+03:00` : e.target.value }
                      : x)))} />
                </label>
              ))}
              <button type="button" className="btn btn-secondary sm:col-span-2" onClick={() => setOverrides(overrides.filter((x) => x.uiId !== o.uiId))}>
                حذف الموعد البديل
              </button>
            </fieldset>
          ))}
          <button type="button" className="btn btn-secondary mt-2"
            onClick={() => setOverrides([...overrides, { uiId: crypto.randomUUID(), trackKey: tracks[0].key, originalDate: '', originalStartLocal: '', startTime: '', endTime: '' }])}>
            إضافة موعد بديل
          </button>
        </details>

        {hold.isError && <p role="alert" className="rounded border border-danger p-3 text-sm text-danger">{learningPlanError(hold.error)}</p>}
      </div>
    </Modal>
  );
}
